import React from "react";

export interface GuestCounts {
    adults: number;
    infants: number;
    petInSeat: number;
    petInCarrier: number;
    serviceAnimal: number;
}

interface AeroGuestsDropdownProps {
    guests: GuestCounts;
    onGuestsChange: (guests: GuestCounts) => void;
    onDone: () => void;
}

const BORDER = "#282828";
const MUTED = "#949494";
const DIM = "#757575";

const MinusIcon: React.FC<{ disabled?: boolean }> = ({ disabled }) => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 48 48"
        fill={disabled ? DIM : "#fff"}
    >
        <path fillRule="evenodd" d="M36 25.5H12v-3h24v3Z" clipRule="evenodd" />
    </svg>
);

const PlusIcon: React.FC<{ disabled?: boolean }> = ({ disabled }) => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 48 48"
        fill={disabled ? DIM : "#fff"}
    >
        <path
            fillRule="evenodd"
            d="M25.5 22.5V12h-3v10.5H12v3h10.5V36h3V25.5H36v-3H25.5Z"
            clipRule="evenodd"
        />
    </svg>
);

const ChevronDownIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" fill={DIM}>
        <path d="M24 31 11 21v-4l13 10 13-10v4L24 31Z" />
    </svg>
);

const PawIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" fill={DIM}>
        <path
            fillRule="evenodd"
            d="M24 20c-3.31 0-6-3.13-6-7s2.69-7 6-7 6 3.13 6 7-2.69 7-6 7Zm-1.3 4.25c.86-.2 1.74-.2 2.6 0C28.2 24.9 35 27.07 35 33c0 12-22 12-22 0 0-5.93 6.79-8.1 9.7-8.75ZM6 20c0 3.87 2.69 7 6 7s6-3.13 6-7-2.69-7-6-7-6 3.13-6 7Zm30 7c-3.31 0-6-3.13-6-7s2.69-7 6-7 6 3.13 6 7-2.69 7-6 7Z"
            clipRule="evenodd"
        />
    </svg>
);

interface RowProps {
    label: string;
    subtitle?: string;
    value: number;
    onDecrease: () => void;
    onIncrease: () => void;
    decreaseDisabled: boolean;
    increaseDisabled: boolean;
    isLast?: boolean;
}

const GuestRow: React.FC<RowProps> = ({
    label,
    subtitle,
    value,
    onDecrease,
    onIncrease,
    decreaseDisabled,
    increaseDisabled,
    isLast,
}) => (
    <div
        className="flex items-center justify-between"
        style={{
            height: 96,
            borderBottom: isLast ? "none" : `1px solid ${BORDER}`,
        }}
    >
        <div>
            <div
                style={{
                    fontSize: 28,
                    fontWeight: 400,
                    lineHeight: "32px",
                    color: "#fff",
                }}
            >
                {label}
            </div>
            {subtitle && (
                <div
                    style={{
                        fontSize: 13,
                        lineHeight: "20px",
                        color: MUTED,
                        marginTop: 2,
                    }}
                >
                    {subtitle}
                </div>
            )}
        </div>
        <div className="flex items-center" style={{ gap: 8 }}>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onDecrease();
                }}
                disabled={decreaseDisabled}
                className="flex items-center justify-center cursor-pointer disabled:cursor-not-allowed"
                style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    opacity: decreaseDisabled ? 0.3 : 1,
                }}
                aria-label={`Decrease ${label}`}
            >
                <MinusIcon disabled={decreaseDisabled} />
            </button>
            <span
                style={{
                    fontSize: 28,
                    fontWeight: 400,
                    lineHeight: "32px",
                    color: "#fff",
                    width: 24,
                    textAlign: "center" as const,
                }}
            >
                {value}
            </span>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onIncrease();
                }}
                disabled={increaseDisabled}
                className="flex items-center justify-center cursor-pointer disabled:cursor-not-allowed"
                style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    opacity: increaseDisabled ? 0.3 : 1,
                }}
                aria-label={`Increase ${label}`}
            >
                <PlusIcon disabled={increaseDisabled} />
            </button>
        </div>
    </div>
);

const AeroGuestsDropdown: React.FC<AeroGuestsDropdownProps> = ({
    guests,
    onGuestsChange,
    onDone,
}) => {
    const { adults, infants } = guests;

    const update = (partial: Partial<GuestCounts>) => {
        onGuestsChange({ ...guests, ...partial });
    };

    // When reducing adults, clamp infants
    const handleAdultsChange = (newAdults: number) => {
        const ni = Math.min(infants, newAdults);
        onGuestsChange({ ...guests, adults: newAdults, infants: ni });
    };

    return (
        <div
            className="absolute"
            style={{
                zIndex: 90,
                top: "calc(100% + 1px)",
                left: -1,
                width: 458,
                backgroundColor: "#000",
                borderBottom: `1px solid ${BORDER}`,
                borderLeft: `1px solid ${BORDER}`,
                borderRight: `1px solid ${BORDER}`,
                borderTop: "none",
                borderRadius: "0 0 8px 8px",
                padding: 16,
                fontFamily:
                    '"Haas Grot Text Web", "Helvetica Neue", Helvetica, Arial, sans-serif',
                maxHeight: "75vh",
                overflowY: "auto" as const,
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Adult — functional */}
            <GuestRow
                label={adults === 1 ? "Adult" : "Adults"}
                subtitle="Ages 2+"
                value={adults}
                onDecrease={() => handleAdultsChange(Math.max(1, adults - 1))}
                onIncrease={() => handleAdultsChange(Math.min(9, adults + 1))}
                decreaseDisabled={adults <= 1}
                increaseDisabled={adults >= 9}
            />

            {/* Infants — functional, max = adults */}
            <GuestRow
                label={infants <= 1 ? "Infants" : "Infants"}
                value={infants}
                onDecrease={() => update({ infants: Math.max(0, infants - 1) })}
                onIncrease={() => update({ infants: infants + 1 })}
                decreaseDisabled={infants <= 0}
                increaseDisabled={infants >= adults}
            />

            {/* Pet (In Seat) — cosmetic only */}
            <GuestRow
                label="Pet (In Seat)"
                subtitle="An extra seat for your pet"
                value={0}
                onDecrease={() => {}}
                onIncrease={() => {}}
                decreaseDisabled
                increaseDisabled
            />

            {/* Pet (In Carrier) — cosmetic only */}
            <GuestRow
                label="Pet (In Carrier)"
                subtitle="Pet & carrier (max size/weight: 20 in x 12 in x 9 in, <20 lbs) must go and fit under the seat."
                value={0}
                onDecrease={() => {}}
                onIncrease={() => {}}
                decreaseDisabled
                increaseDisabled
            />

            {/* Service Animal — cosmetic only */}
            <GuestRow
                label="Service Animal*"
                subtitle="Service animal must fit within passenger's foot space"
                value={0}
                onDecrease={() => {}}
                onIncrease={() => {}}
                decreaseDisabled
                increaseDisabled
                isLast
            />

            {/* PET DETAILS* accordion (collapsed, cosmetic) */}
            <div
                className="flex items-center"
                style={{
                    borderTop: `1px solid ${BORDER}`,
                    paddingTop: 20,
                    paddingBottom: 20,
                    gap: 8,
                    cursor: "pointer",
                }}
            >
                <PawIcon />
                <span
                    style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: DIM,
                        textTransform: "uppercase" as const,
                        letterSpacing: "0.05em",
                    }}
                >
                    Pet details*
                </span>
                <div className="ml-auto">
                    <ChevronDownIcon />
                </div>
            </div>

            {/* Done button */}
            <div
                className="flex justify-end"
                style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 8 }}
            >
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDone();
                    }}
                    className="cursor-pointer"
                    style={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: "#fff",
                        background: "none",
                        border: "none",
                        padding: 0,
                    }}
                >
                    Done
                </button>
            </div>
        </div>
    );
};

export default AeroGuestsDropdown;
